import { useTranslation } from "react-i18next";
import { format } from "date-fns";

import { WorkdayCollapsibleTile, WorkdayCollapsibleTileHandle } from "../WorkdayCollapsibleTile";
import { Workday } from "@lib/store/src/data/timeslots/apiTypes";
import { selectTimeslot, useAppDispatch, useAppSelector } from "@lib/store";
import {
  getConfirmedWorkspaceReservationsForWorkday,
  getWorkdaysForDate,
} from "@lib/store/src/data/timeslots/selectors";
import { groupBy } from "@lib/utils";
import { WorkspaceReservationTile } from "./WorkspaceReservationTile";
import { TileMapPreview } from "../../CalendarDayOverview/components/TileMapPreview";
import { BookingFailedLabel } from "../../CalendarWeekDay/components/BookingFailedLabel";
import { BookWorkspaceCard } from "src/components/organisms/bookWorkspace";
import { useModal } from "@hooks";
import { isOfficeWorkday } from "@lib/store/src/data/timeslots/utils";
import { useWorkdayIsIncomplete } from "@lib/logic";

interface WorkdayReservationsSectionProps {
  workday: Workday | undefined;
  date: Date;
}

export const WorkdayReservationsSection = ({ workday, date }: WorkdayReservationsSectionProps) => {
  const { t } = useTranslation();
  const { setModalPages, openModal } = useModal();
  const dispatch = useAppDispatch();

  const dateKey = format(date, "yyyy-MM-dd");
  const workdays = useAppSelector(state => getWorkdaysForDate(state, dateKey));
  const workspaceReservations = useAppSelector(state =>
    getConfirmedWorkspaceReservationsForWorkday(state, workday?.id ?? null),
  );
  const isEmpty = !workday || workspaceReservations.length === 0;

  const reservationsByNodeId = Object.entries(groupBy(workspaceReservations, r => r.workspace.nodeId)).map(
    ([nodeId, r]) => ({
      nodeId,
      reservations: r,
    }),
  );

  const isIncompleteBooking = useWorkdayIsIncomplete(workday ?? null);

  if (isIncompleteBooking) {
    return (
      <WorkdayCollapsibleTileHandle
        className="border-t"
        icon="desk"
        isOpen={false}
        isToggleable={false}
        onClick={() => {
          if (!isOfficeWorkday(workday)) {
            console.warn("Non-office day can never be incomplete");
            return;
          }

          // Note: this is so easy to forget... should not be required
          //       to use this modal card.
          dispatch(
            selectTimeslot({
              startTime: workday.localStart,
              endTime: workday.localEnd,
            }),
          );

          setModalPages([<BookWorkspaceCard workday={workday} />]);
          openModal();
        }}>
        <BookingFailedLabel />
      </WorkdayCollapsibleTileHandle>
    );
  }

  if (isEmpty) {
    return null;
  }

  return (
    <WorkdayCollapsibleTile
      handle={t("timeslots:DeskBooking", {
        count: workspaceReservations.length,
      })}
      icon="desk"
      initialOpen={!!workdays?.data && workdays.data.length === 1 && workspaceReservations.length === 1}
      contentClassName="p-0"
      handleClassName="border-t">
      <div className="min-h-[100px]">
        {reservationsByNodeId.map(({ nodeId, reservations }) => (
          <div
            key={nodeId}
            className="p-0">
            {/* Grouped by reservation, take first */}
            <div className="border-t">
              <TileMapPreview workspaceReservation={reservations[0]} />
            </div>

            {reservations.map((workspaceReservation, index) => (
              <WorkspaceReservationTile
                key={workspaceReservation.id}
                workspaceReservation={workspaceReservation}
                workday={workday}
                selectedDate={dateKey}
                isLast={index === reservations.length - 1}
              />
            ))}
          </div>
        ))}
      </div>
    </WorkdayCollapsibleTile>
  );
};
